// © 2026 AttainIP Engineering

/**
 * Debug-view state for the chat panel.
 *
 * Two layers:
 *
 * 1. **Build-time env flag** (`VITE_ASSISTANT_DEBUG_MODE` in each app) —
 *    decides whether the toggle is offered at all. Off in production.
 *
 * 2. **Per-user runtime preference** (this module) — when the toggle IS
 *    offered, the user can flip between "Live" and "Debug" view of tool
 *    calls. Persisted to localStorage so the choice survives reloads.
 *
 * In Live view, ``ToolCallCard`` shows only the one-line summary —
 * the expand chevron is hidden and the Request / Response detail panes
 * are not rendered. In Debug view, the current expandable widget is
 * shown. Default when the toggle is offered: Live.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'attain-assistant-debug-view';

export type DebugViewMode = 'live' | 'debug';

interface DebugViewContextValue {
  /** Current view (live | debug). Always defined; ``live`` when no provider. */
  view: DebugViewMode;
  /** True when the env-enabled toggle is available (i.e. provider mounted with ``available``). */
  toggleAvailable: boolean;
  /** Flip between live and debug. No-op when ``toggleAvailable`` is false. */
  setView: (next: DebugViewMode) => void;
}

const DebugViewContext = createContext<DebugViewContextValue>({
  view: 'live',
  toggleAvailable: false,
  setView: () => undefined,
});

/** Read consumer-side. Defaults to ``live`` outside any provider. */
export function useDebugView(): DebugViewContextValue {
  return useContext(DebugViewContext);
}

/** Read+persist hook used by the provider. */
function usePersistedView(initial: DebugViewMode = 'live') {
  const [view, setViewState] = useState<DebugViewMode>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === 'debug' || stored === 'live' ? stored : initial;
    } catch {
      return initial;
    }
  });

  const setView = useCallback((next: DebugViewMode) => {
    setViewState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be unavailable (private mode); ignore.
    }
  }, []);

  // Keep multiple tabs in sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === 'debug' || e.newValue === 'live') {
        setViewState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return [view, setView] as const;
}

interface DebugViewProviderProps {
  /** When false, the toggle is not offered — view is locked to ``live``. */
  available: boolean;
  children: React.ReactNode;
}

export function DebugViewProvider({ available, children }: DebugViewProviderProps) {
  const [persistedView, setPersistedView] = usePersistedView('live');
  const view: DebugViewMode = available ? persistedView : 'live';
  const setView = available ? setPersistedView : () => undefined;

  return (
    <DebugViewContext.Provider value={{ view, toggleAvailable: available, setView }}>
      {children}
    </DebugViewContext.Provider>
  );
}
